import { formatCompactCurrency } from '../../functions/calculations';
import type { PropertyExplorerRow } from '../../functions/calculations';

interface PropertyExplorerTableProps {
  rows: PropertyExplorerRow[];
  currencyCode: string;
  onSelectProperty?: (unitId: number) => void;
}

const COLUMN_HEADERS = [
  { id: 'name', label: 'Property Name', numeric: false },
  { id: 'location', label: 'Location', numeric: false },
  { id: 'operatingModel', label: 'Operating Model', numeric: false },
  { id: 'ownership', label: 'Ownership %', numeric: true },
  { id: 'marketValue', label: 'Market Value', numeric: true },
  { id: 'netEquity', label: 'Net Equity', numeric: true },
  { id: 'noi', label: 'NOI', numeric: true },
  { id: 'netCashFlow', label: 'Net Cash Flow', numeric: true },
  { id: 'cocReturn', label: 'CoC Return', numeric: true },
  { id: 'attention', label: 'Attention', numeric: false },
];

function formatOwnership(percent: number): string {
  return Number.isInteger(percent) ? `${percent}%` : `${percent.toFixed(1)}%`;
}

function formatReturn(percent: number | null): string {
  return percent === null ? '—' : `${percent.toFixed(1)}%`;
}

function formatCurrencyCell(value: number | null, currencyCode: string): string {
  return value === null ? '—' : formatCompactCurrency(value, currencyCode);
}

/** Signed values are tinted so gains and shortfalls are scannable at a glance. */
function toneClass(value: number | null): string {
  if (value === null || value === 0) return '';
  return value > 0 ? ' property-explorer__value--positive' : ' property-explorer__value--negative';
}

export function PropertyExplorerTable({
  rows,
  currencyCode,
  onSelectProperty,
}: PropertyExplorerTableProps) {
  return (
    <div className="property-explorer__table-wrap">
      <table className="property-explorer__table">
        <thead>
          <tr>
            {COLUMN_HEADERS.map((column) => (
              <th
                key={column.id}
                scope="col"
                className={
                  column.numeric
                    ? 'property-explorer__header property-explorer__header--numeric'
                    : 'property-explorer__header'
                }
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.unitId}
              className={`property-explorer__row${
                row.attention.length > 0 ? ' property-explorer__row--attention' : ''
              }`}
            >
              <td className="property-explorer__cell property-explorer__cell--name">
                {onSelectProperty ? (
                  <button
                    type="button"
                    className="property-explorer__name-button"
                    onClick={() => onSelectProperty(row.unitId)}
                  >
                    {row.name}
                  </button>
                ) : (
                  row.name
                )}
              </td>
              <td className="property-explorer__cell">{row.locationLabel}</td>
              <td className="property-explorer__cell">{row.operatingModel}</td>
              <td className="property-explorer__cell property-explorer__cell--numeric">
                {formatOwnership(row.ownershipPercent)}
              </td>
              <td className="property-explorer__cell property-explorer__cell--numeric">
                <span className="property-explorer__value">
                  {formatCompactCurrency(row.marketValue, currencyCode)}
                </span>
                {row.marketValueNative && (
                  <span className="property-explorer__value-native">
                    {formatCompactCurrency(
                      row.marketValueNative.value,
                      row.marketValueNative.currencyCode
                    )}{' '}
                    native
                  </span>
                )}
              </td>
              <td className="property-explorer__cell property-explorer__cell--numeric">
                {formatCurrencyCell(row.netEquity, currencyCode)}
              </td>
              <td className="property-explorer__cell property-explorer__cell--numeric">
                <span className={`property-explorer__value${toneClass(row.noi)}`}>
                  {formatCurrencyCell(row.noi, currencyCode)}
                </span>
              </td>
              <td className="property-explorer__cell property-explorer__cell--numeric">
                <span className={`property-explorer__value${toneClass(row.netCashFlow)}`}>
                  {formatCurrencyCell(row.netCashFlow, currencyCode)}
                </span>
              </td>
              <td className="property-explorer__cell property-explorer__cell--numeric">
                <span className={`property-explorer__value${toneClass(row.cocReturn)}`}>
                  {formatReturn(row.cocReturn)}
                </span>
              </td>
              <td className="property-explorer__cell property-explorer__cell--attention">
                {row.attention.length === 0 ? (
                  <span className="property-explorer__no-flag">—</span>
                ) : (
                  row.attention.map((flag) => (
                    <span key={flag.id} className="property-explorer__badge">
                      {flag.label}
                    </span>
                  ))
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
